import {createSlice} from '@reduxjs/toolkit';

import {MODAL_TYPES} from '../../consts/modal/modalTypes';
import storeSliceNames from '../../consts/storeSliceNames';
import {dispatch} from '../store';

const initialState = {
    globalModals: [],
};

const DEFAULT_MODAL_ORDER = 1000;
const IMPORTANT_MODAL_ORDER = 1100;

const modalSlice = createSlice({
    name: storeSliceNames.modal,
    initialState,
    reducers: {
        showModal(state, action) {
            const {modalType, modalProps, order = DEFAULT_MODAL_ORDER, alwaysDisplay = false} = action.payload;
            const isModalExists = state.globalModals.some((modal) => modal.modalType === modalType);

            if (isModalExists) return;
            state.globalModals.push({modalType, modalProps, order, alwaysDisplay});
        },
        hideModal(state, action) {
            const modalType = action.payload;

            state.globalModals = state.globalModals.filter((modal) => modal.modalType !== modalType);
        },
        hideAllModals(state) {
            state.globalModals = [];
        },
    },
});

export const showModal = (modal) => modalSlice.actions.showModal(modal);
export const hideModal = (modalType) => modalSlice.actions.hideModal(modalType);
export const hideAllModals = () => modalSlice.actions.hideAllModals();

export const showLteConnectionGuideErrorModal = (errorType) =>
    showModal({modalType: MODAL_TYPES.LTE_CONNECTION_GUIDE_ERROR_MODAL, alwaysDisplay: true, modalProps: {errorType}});

export const showRecommendationsModal = (modalProps) =>
    showModal({modalType: MODAL_TYPES.CONNECTION_GUIDE_RECOMMENDATIONS_MODAL, alwaysDisplay: true, modalProps});

export const showExperienceScoreCalibrationModal = (modalProps) =>
    showModal({modalType: MODAL_TYPES.EXPERIENCE_SCORE_CALIBRATION_MODAL, modalProps});

export const showExperienceInsightsConsentModal = (modalProps) =>
    showModal({modalType: MODAL_TYPES.EXPERIENCE_INSIGHTS_CONSENT_MODAL, modalProps});

export const showUsageConsentsModal = (modalProps) =>
    showModal({modalType: MODAL_TYPES.USAGE_CONSENTS_MODAL, modalProps});

export const showUsageModal = (modalProps) => showModal({modalType: MODAL_TYPES.USAGE_MODAL, modalProps});

export const showUsageUwmModal = (modalProps) => showModal({modalType: MODAL_TYPES.USAGE_UWM_MODAL, modalProps});

export const showUsagePageSelectStickModal = (modalProps) =>
    showModal({modalType: MODAL_TYPES.USAGE_SELECT_STICK_MODAL, alwaysDisplay: true, modalProps});

export const showClubPageQuizModal = (modalProps) =>
    showModal({modalType: MODAL_TYPES.CLUB_PAGE_QUIZ_MODAL, modalProps});

export const showClubPagePuzzleModal = (modalProps) =>
    showModal({modalType: MODAL_TYPES.CLUB_PAGE_PUZZLE_MODAL, modalProps});

export const showClubPageArticleModal = (modalProps) =>
    showModal({modalType: MODAL_TYPES.CLUB_PAGE_ARTICLE_MODAL, modalProps});

export const showClubPageStories = (modalProps) =>
    showModal({
        modalType: MODAL_TYPES.CLUB_PAGE_STORIES,
        modalProps,
        alwaysDisplay: true,
    });

export const showClubPagePromptCompletedModal = (modalProps) =>
    showModal({modalType: MODAL_TYPES.CLUB_PAGE_PROMPT_COMPLETED, modalProps, alwaysDisplay: true});

export const showClubPagePromptRewardsModal = (modalProps) =>
    showModal({
        modalType: MODAL_TYPES.CLUB_PAGE_PROMPT_REWARDS,
        modalProps,
        alwaysDisplay: true,
        order: IMPORTANT_MODAL_ORDER,
    });

export const showAboutDeviceInfoModal = (modalProps) =>
    showModal({modalType: MODAL_TYPES.DEVICE_SETTINGS_ABOUT_DEVICE_INFO_MODAL, modalProps});

export const showLastSeenLocationModal = (modalProps) =>
    showModal({modalType: MODAL_TYPES.DEVICE_SETTINGS_LOCATION_MODAL, modalProps});

export const showFirmwareUpdateCompleteModal = (modalProps) =>
    showModal({modalType: MODAL_TYPES.SOFTWARE_UPDATE_COMPLETE, modalProps});

export const showClubPageRewardCardModal = (modalProps) =>
    showModal({modalType: MODAL_TYPES.CLUB_PAGE_REWARD_CARD_MODAL, modalProps});

export const showClubPageRewardListModal = (modalProps) =>
    showModal({modalType: MODAL_TYPES.CLUB_PAGE_REWARD_LIST_MODAL, modalProps, alwaysDisplay: true});

export const showCredentialsModal = (modalProps) =>
    showModal({modalType: MODAL_TYPES.CREDENTIALS_MODAL, modalProps, alwaysDisplay: true});

export const showFeedbackModal = (modalProps) =>
    showModal({modalType: MODAL_TYPES.CLUB_PAGE_FEEDBACK_MODAL, modalProps, alwaysDisplay: true});

export const showClubPageReportModal = (modalProps) =>
    showModal({modalType: MODAL_TYPES.CLUB_PAGE_REPORT_MODAL, modalProps, alwaysDisplay: true});

export const showFeedbackMobileUwmModal = (modalProps) =>
    showModal({modalType: MODAL_TYPES.FEEDBACK_MOBILE_UWM_MODAL, modalProps});

export const showOrionFeedbackUwmModal = (modalProps) =>
    showModal({modalType: MODAL_TYPES.ORION_FEEDBACK_UWM_MODAL, modalProps});

export const showHighTemperatureModal = (modalProps) =>
    showModal({modalType: MODAL_TYPES.HIGH_TEMPERATURE_MODAL, modalProps});

export const showNotificationsFeedbackModal = (modalProps) =>
    showModal({modalType: MODAL_TYPES.NOTIFICATIONS_FEEDBACK_MODAL, modalProps});

export const showSocialsModal = (modalProps) =>
    showModal({modalType: MODAL_TYPES.SOCIALS_MODAL, modalProps, alwaysDisplay: true});

export const showStoresModal = (modalProps) =>
    showModal({modalType: MODAL_TYPES.STORES_MODAL, modalProps, alwaysDisplay: true});

export const showContactUsModal = (modalProps) =>
    showModal({modalType: MODAL_TYPES.CONTACT_US_MODAL, modalProps, alwaysDisplay: true});

export const showForgotPasswordModal = (modalProps) =>
    showModal({modalType: MODAL_TYPES.FORGOT_PASSWORD_MODAL, modalProps, alwaysDisplay: true});

export const showReconnectGuideModal = (modalProps) =>
    showModal({modalType: MODAL_TYPES.RECONNECT_GUIDE_MODAL, modalProps, alwaysDisplay: true});

export const showAiAssistantConsentModal = () =>
    showModal({modalType: MODAL_TYPES.AI_ASSISTANT_CONSENT_MODAL, alwaysDisplay: true});

export const showAiAssistantLoginModal = () =>
    showModal({modalType: MODAL_TYPES.AI_ASSISTANT_LOGIN_MODAL, alwaysDisplay: true});

export const showPreferenceCenterModal = (modalProps) =>
    showModal({modalType: MODAL_TYPES.PREFERENCE_CENTER_MODAL, modalProps, alwaysDisplay: true});

export const showAiAssistantMicrophonePermissionModal = () =>
    showModal({modalType: MODAL_TYPES.AI_ASSISTANT_MICROPHONE_PERMISSION_MODAL, alwaysDisplay: true});

export const showAiAssistantMicrophoneDeniedModal = () =>
    showModal({modalType: MODAL_TYPES.AI_ASSISTANT_MICROPHONE_DENIED_MODAL, alwaysDisplay: true});

export const showTipsAndTutorialsModal = (modalProps) =>
    showModal({modalType: MODAL_TYPES.TIPS_AND_TUTORIALS_MODAL, modalProps, alwaysDisplay: true});

export const showTipsAndTutorialsMediaModal = (modalProps) =>
    showModal({modalType: MODAL_TYPES.TIPS_AND_TUTORIALS_MEDIA_MODAL, modalProps, alwaysDisplay: true});

export default modalSlice.reducer;

window.openFeedbackMobileUwm = (feature) => dispatch(showFeedbackMobileUwmModal({feature}));

window.openOrionFeedbackUwm = (insightId, heatingProfile) =>
    dispatch(showOrionFeedbackUwmModal({insightId, heatingProfile}));

window.openUsageUwm = (defaultStickType) => dispatch(showUsageUwmModal({defaultStickType}));

window.openHighTemperatureModal = () => dispatch(showHighTemperatureModal());

window.openNotificationsFeedbackModal = () => dispatch(showNotificationsFeedbackModal());
